import { FC, ReactNode, useMemo, useRef } from 'react'
import Map, { Source, Layer, Marker, useMap } from 'react-map-gl/mapbox'
import arrowHead from '@images/arrowHeadOrange.png'
import directionArrow from '@images/directionArrow.png'
import uTurnIcon from '@images/uTurn.png'

import 'mapbox-gl/dist/mapbox-gl.css'

import {
  determineDestinationNode,
  generateDetectionsGeoJSON,
  generateDirectionsGeoJSON,
  generateDriveSegmentGeoJSON,
  generateSegmentLabelsGeoJSON,
  generateSignPostsGeoJSON,
  useMapImage,
} from './utils/mapGLHelpers'
import { MantineColorScheme, useMantineTheme } from '@mantine/core'
import { getSegmentColors } from './utils/utils'
import {
  DirectionsRouteResponse,
  DisplayableEventsResponse,
  NavigationDataResponse,
} from '@/api/navigationApi/types.gen'
import { createArrayHash, createCurrentSegmentHash, determineMapCenter, onMapLoad } from './utils/mapUtils'
import { DISPLAY_DETECTIONS, DISPLAY_SIGN_POSTS, MAPBOX_PUBLIC_TOKEN } from '@/constants'
import { UseQueryResult } from '@tanstack/react-query'

export const shouldShowStreetSegmentArrows = (
  navigationData: NavigationDataResponse | undefined,
  directionsEnabled: boolean
): boolean => {
  if (navigationData?.is_freeride) return false
  if (navigationData?.route_module_state === 'Driving') return true
  return (
    directionsEnabled &&
    (navigationData?.route_module_state === 'Paused' || navigationData?.route_module_state === 'Fetched')
  )
}

const NavigationMap: FC<{
  id: string
  cursor: 'hidden' | 'regular' | 'lowGpsAccuracy'
  cursorAzimuth?: number
  colorScheme: MantineColorScheme
  children?: ReactNode
  navigationData?: NavigationDataResponse
  directionsData?: DirectionsRouteResponse
  directionsEnabled: boolean
  initialZoom: number
  width?: string | number
  height?: string | number
  allowScrollZoom?: boolean
  detectionsQuery: UseQueryResult<DisplayableEventsResponse>
}> = ({
  id,
  cursor,
  children,
  colorScheme,
  navigationData,
  directionsData,
  directionsEnabled,
  initialZoom,
  width = '100%',
  height = '100vh',
  detectionsQuery,
}) => {
  const theme = useMantineTheme()
  const navigationMap = useMap()[id]

  useMapImage(navigationMap, 'directionArrow', directionArrow, { width: 16, height: 16 })
  useMapImage(navigationMap, 'arrowHead', arrowHead, { width: 64, height: 64 })
  useMapImage(navigationMap, 'uTurnIcon', uTurnIcon, { width: 48, height: 48 })

  const futureSegmentsHash = useMemo(
    () => createArrayHash(navigationData?.future_street_segments),
    [navigationData?.future_street_segments]
  )

  const currentSegmentHash = useMemo(
    () => createCurrentSegmentHash(navigationData?.current_street_segment),
    [navigationData?.current_street_segment]
  )

  const detectionsForMap = useMemo(
    () => (DISPLAY_DETECTIONS ? (detectionsQuery?.data?.vehicle_evidence_data_to_display?.slice(-30) ?? []) : []),
    [detectionsQuery?.data?.vehicle_evidence_data_to_display]
  )

  const signPostsForMap = useMemo(
    () => (DISPLAY_SIGN_POSTS ? (detectionsQuery?.data?.signpost_evidence_data_to_display ?? []) : []),
    [detectionsQuery?.data?.signpost_evidence_data_to_display]
  )

  const isPreDrive =
    navigationData?.route_module_state === 'Fetched' ||
    (navigationData?.route_module_state === 'Paused' && !navigationData?.current_street_segment)

  const prevFutureRef = useRef<GeoJSON.FeatureCollection<GeoJSON.LineString> | null>(null)
  const prevFutureHashRef = useRef('')

  const futureSegments = useMemo(() => {
    const combinedHash = `${futureSegmentsHash}-${currentSegmentHash}-${isPreDrive}`
    if (combinedHash === prevFutureHashRef.current && prevFutureRef.current) {
      return prevFutureRef.current
    }

    const segments = isPreDrive
      ? navigationData?.future_street_segments?.slice(0, 1)
      : navigationData?.future_street_segments

    const result = generateDriveSegmentGeoJSON(
      segments,
      navigationData?.current_street_segment,
      getSegmentColors
    ).futureSegmentsGeoJson

    prevFutureRef.current = result ?? null
    prevFutureHashRef.current = combinedHash
    return result
  }, [futureSegmentsHash, currentSegmentHash, navigationData, isPreDrive])

  const currentSegment = useMemo(() => {
    return generateDriveSegmentGeoJSON(undefined, navigationData?.current_street_segment, getSegmentColors)
      .currentSegmentGeoJson
    // currentSegmentHash is the stable identity for current_street_segment;
    // navigationData is included so the memo body has the latest reference
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentSegmentHash, navigationData])

  const segmentDirections = useMemo(() => {
    const segments = []
    if (navigationData?.current_street_segment) segments.push(navigationData.current_street_segment)
    for (const s of navigationData?.future_street_segments ?? []) {
      if (!segments.find(it => it.street_segment_id === s.street_segment_id)) segments.push(s)
    }
    return generateDriveSegmentGeoJSON(segments).futureSegmentsGeoJson
    // hashes are stable identities; navigationData provides the latest reference
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [futureSegmentsHash, currentSegmentHash, navigationData])

  const uTurnAnchor = useMemo(() => {
    const curr = navigationData?.current_street_segment
    const next = navigationData?.future_street_segments?.[0]
    if (curr && next && curr.street_segment_id === next.street_segment_id) {
      return determineDestinationNode(curr)
    }
    return undefined
  }, [navigationData])

  const uTurnPointGeoJSON = useMemo(
    (): GeoJSON.FeatureCollection<GeoJSON.Point> =>
      uTurnAnchor
        ? {
            type: 'FeatureCollection',
            features: [
              {
                type: 'Feature',
                geometry: {
                  type: 'Point',
                  coordinates: [uTurnAnchor.longitude, uTurnAnchor.latitude],
                },
                properties: {},
              },
            ],
          }
        : { type: 'FeatureCollection', features: [] },
    [uTurnAnchor]
  )

  const directionSegments = useMemo(() => {
    return directionsData ? generateDirectionsGeoJSON(directionsData) : undefined
  }, [directionsData])

  const segmentLabels = useMemo(() => {
    const MAX_LABELS = 5
    const labeled: Array<{ coordinates: number[][]; color: string; label: number }> = []
    let labelNumber = 1

    if (currentSegment && labelNumber <= MAX_LABELS) {
      labeled.push({
        coordinates: currentSegment.geometry.coordinates,
        color: currentSegment.properties.color as string,
        label: labelNumber++,
      })
    }

    if (futureSegments?.features) {
      const inDrivingOrder = [...futureSegments.features].reverse()
      for (const feature of inDrivingOrder) {
        if (labelNumber > MAX_LABELS) break
        labeled.push({
          coordinates: feature.geometry.coordinates as number[][],
          color: feature.properties?.color as string,
          label: labelNumber++,
        })
      }
    }

    return generateSegmentLabelsGeoJSON(labeled)
  }, [futureSegments, currentSegment])

  return (
    <Map
      initialViewState={{ zoom: initialZoom, ...determineMapCenter() }}
      id={id}
      style={{ width, height }}
      mapStyle={`mapbox://styles/mapbox/${colorScheme === 'light' ? 'navigation-guidance-day-v4' : 'navigation-guidance-night-v4'}?optimize=true`}
      mapboxAccessToken={MAPBOX_PUBLIC_TOKEN}
      dragPan={false}
      scrollZoom={false}
      touchZoomRotate={false}
      onLoad={onMapLoad}
      antialias={false}
      fadeDuration={0}
    >
      <Source id="detections" type="geojson" data={generateDetectionsGeoJSON(detectionsForMap, theme.colors.blue[9])}>
        <Layer
          id="detection-points"
          type="circle"
          paint={{
            'circle-color': theme.colors.blue[9],
            'circle-radius': ['interpolate', ['linear'], ['zoom'], 10, 2.5, 14, 3.5, 18, 5],
          }}
        />
      </Source>

      <Source id="signPosts" type="geojson" data={generateSignPostsGeoJSON(signPostsForMap)}>
        <Layer
          id="sign-post-circles"
          type="circle"
          paint={{
            'circle-radius': 5,
            'circle-color': 'white',
            'circle-stroke-width': 1,
            'circle-stroke-color': theme.colors.blue[9],
          }}
        />
      </Source>
      {children}
      {cursor === 'lowGpsAccuracy' && (
        <img
          src={arrowHead}
          alt="accuracy-shadow"
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            zIndex: 9998,
            transform: 'translate(-50%, -50%) scale(0.7)',
            filter: 'opacity(0.3)',
            pointerEvents: 'none',
          }}
        />
      )}
      <img
        src={arrowHead}
        alt="car-cursor"
        style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          zIndex: 9999,
          transform: 'translate(-50%, -50%) scale(0.35) rotate(0deg)',
          visibility: cursor !== 'hidden' ? 'visible' : 'hidden',
          willChange: 'transform',
          pointerEvents: 'none',
        }}
      />
      {!navigationData?.is_freeride && (
        <Source id="futureSegments" type="geojson" data={futureSegments}>
          <Layer
            id="future_segments"
            type="line"
            paint={{ 'line-color': ['get', 'color'], 'line-width': 8 }}
            layout={{
              'line-join': 'round',
              'line-cap': 'round',
            }}
          />
        </Source>
      )}
      {!navigationData?.is_freeride && currentSegment && (
        <Source id="currentSegment" type="geojson" data={currentSegment}>
          <Layer id="layer_02_current_segment" type="line" paint={{ 'line-color': '#fa5252', 'line-width': 8 }} />
        </Source>
      )}
      {directionsEnabled && directionSegments && !navigationData?.is_freeride && (
        <Source id="directionSegments" type="geojson" data={directionSegments}>
          <Layer
            id="layer_04_directions_line"
            type="line"
            beforeId="future_segments"
            paint={{ 'line-color': ['get', 'color'], 'line-width': 8 }}
            layout={{
              'line-join': 'round',
              'line-cap': 'round',
            }}
          />
          <Layer
            id="layer_04_directions_arrow"
            type="symbol"
            beforeId="future_segments"
            layout={{
              'symbol-placement': 'line',
              'icon-image': 'directionArrow',
              'icon-rotate': 90,
              'icon-rotation-alignment': 'map',
              'icon-size': 1.3,
              'symbol-z-order': 'source',
              'symbol-sort-key': 1,
              'icon-allow-overlap': true,
            }}
          />
        </Source>
      )}
      {shouldShowStreetSegmentArrows(navigationData, directionsEnabled) && (
        <Source id="streetSegmentArrows" type="geojson" data={segmentDirections}>
          <Layer
            id="layer_01_arrows"
            type="symbol"
            source="streetSegmentArrows"
            layout={{
              'symbol-placement': 'line',
              'icon-image': 'directionArrow',
              'icon-rotate': 90,
              'icon-size': 1.3,
              'symbol-z-order': 'source',
              'symbol-sort-key': 1,
              'icon-allow-overlap': true,
            }}
          />
        </Source>
      )}
      {!navigationData?.is_freeride &&
        segmentLabels.features.map(feature => {
          const [longitude, latitude] = feature.geometry.coordinates
          const { label, color, textColor } = feature.properties as {
            label: string
            color: string
            textColor: string
          }
          return (
            <Marker key={label} longitude={longitude} latitude={latitude} anchor="center">
              <div
                style={{
                  width: 28,
                  height: 28,
                  borderRadius: '50%',
                  backgroundColor: color,
                  color: textColor,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 700,
                  fontSize: 14,
                  lineHeight: 1,
                  boxShadow: '0 1px 3px rgba(0,0,0,0.35)',
                  pointerEvents: 'none',
                  userSelect: 'none',
                }}
              >
                {label}
              </div>
            </Marker>
          )
        })}
      {!navigationData?.is_freeride && (
        <Source id="uTurnPoint" type="geojson" data={uTurnPointGeoJSON}>
          <Layer
            id="u-turn-icon"
            type="symbol"
            layout={{
              'icon-image': 'uTurnIcon',
              'icon-anchor': 'bottom',
              'icon-size': 1,
              'icon-allow-overlap': true,
            }}
          />
        </Source>
      )}
    </Map>
  )
}

export default NavigationMap
